import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FiMail, FiPhone, FiMapPin, FiSend, FiCheckCircle } from "react-icons/fi";
import Hero from '../components/nav/Hero';
import blogServices from '../services/BlogServices';
import FadeInSection from '../components/nav/FadeInSection';

function ContactPage() {
    const url = window.location.href
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        service_needed: 'Manning Services',
        message: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            await blogServices.createInquiry(formData);
            setIsSubmitted(true);
            setFormData({ name: '', email: '', phone: '', service_needed: 'Manning Services', message: '' });
        } catch (err) {
            console.error(err);
            setError('Something went wrong. Please try again or call us directly at +254 117666000.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-base-100 min-h-screen">
            <Helmet>
                <title>Contact Guardhart Security | Get a Quote & Security Consultation Kenya</title>
                <link rel="canonical" href={url} />
                <meta name="description" content="Contact Guardhart Security Services for professional security solutions in Kenya. Call +254 117666000 or email info@guardhart.co.ke for guards, CCTV, close protection, and security audits. 24/7 response across Nairobi, Kisumu, Mombasa, Nakuru, and Eldoret." />

                <meta property="og:type" content="website" />
                <meta property="og:url" content={url} />
                <meta property="og:title" content="Contact Guardhart Security | Get a Quote & Security Consultation Kenya" />
                <meta property="og:description" content="Contact Guardhart Security Services for professional security solutions in Kenya. Call +254 117666000 or email info@guardhart.co.ke. 24/7 response across Nairobi, Kisumu, Mombasa, Nakuru, and Eldoret." />

                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Contact Guardhart Security | Get a Quote & Security Consultation Kenya" />
                <meta property="twitter:description" content="Contact Guardhart Security Services for professional security solutions in Kenya. Call +254 117666000 or email info@guardhart.co.ke. 24/7 response nationwide." />
            </Helmet>

            <Hero
                label="Get in Touch"
                title="Let's secure what matters."
                description="Whether you need guards, CCTV, or a full security audit, our team is ready to help. Reach out and we'll respond within 24 hours."
                primaryBtnText="Call Us Now"
                primaryBtnLink="tel:+254714843383"
                secondaryBadgeText="24hr Response"
            />
            <FadeInSection>
                <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">

                        {/* Left Column: Contact Information */}
                        <div className="space-y-10">
                            <div>
                                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-base-content mb-4">
                                    Get in touch
                                </h2>
                                <p className="text-zinc-600 leading-relaxed">
                                    Ready to secure your assets? Reach out to our team for a consultation, custom quote, or general inquiry. We are here to help.
                                </p>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-lg bg-base-200 border border-zinc-200 text-primary shrink-0">
                                        <FiPhone className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-600 mb-1">Phone</h3>
                                        <a href="tel:+254714843383" className="text-base-content hover:text-primary transition-colors">
                                            +254 117 666 000
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-lg bg-base-200 border border-zinc-200 text-primary shrink-0">
                                        <FiMail className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-600 mb-1">Email</h3>
                                        <a href="mailto:info@guardhart.co.ke" className="text-base-content hover:text-primary transition-colors">
                                            sales@guardhart.co.ke
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="p-3 rounded-lg bg-base-200 border border-zinc-200 text-primary shrink-0">
                                        <FiMapPin className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-600 mb-1">Headquarters</h3>
                                        <p className="text-base-content">
                                            Nairobi, Kenya<br />
                                            <span className="text-zinc-500 text-sm">(Six branches nationwide)</span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-zinc-200">
                                <p className="text-sm text-zinc-600">
                                    <span className="text-primary font-semibold">Note:</span> For immediate emergency security response, please call our 24/7 hotline directly.
                                </p>
                            </div>
                        </div>

                        {/* Right Column: Contact Form */}
                        <div className="bg-base-200 p-8 md:p-10 rounded-lg border border-zinc-200">
                            {isSubmitted ? (
                                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                                    <div className="p-4 rounded-full bg-primary/10 text-primary mb-6">
                                        <FiCheckCircle className="w-12 h-12" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-base-content mb-2">Message Sent</h3>
                                    <p className="text-zinc-600">Thank you for reaching out. Our team will review your inquiry and respond within 24 hours.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label htmlFor="name" className="text-sm font-medium text-zinc-600">Full Name *</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full bg-base-100 border border-zinc-200 rounded px-4 py-3 text-base-content placeholder-zinc-400 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all"
                                                placeholder="John Doe"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="email" className="text-sm font-medium text-zinc-600">Email Address *</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full bg-base-100 border border-zinc-200 rounded px-4 py-3 text-base-content placeholder-zinc-400 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all"
                                                placeholder="john@company.co.ke"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label htmlFor="phone" className="text-sm font-medium text-zinc-600">Phone Number</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full bg-base-100 border border-zinc-200 rounded px-4 py-3 text-base-content placeholder-zinc-400 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all"
                                                placeholder="+254 7XX XXX XXX"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="service" className="text-sm font-medium text-zinc-600">Service Needed</label>
                                            <select
                                                id="service"
                                                name="service_needed"
                                                value={formData.service_needed}
                                                onChange={handleChange}
                                                className="w-full bg-base-100 border border-zinc-200 rounded px-4 py-3 text-base-content focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all appearance-none"
                                                disabled={isSubmitting}
                                            >
                                                <option>Manning Services</option>
                                                <option>Close Protection</option>
                                                <option>Security Systems</option> 
                                                <option>Training & Consulting</option>
                                                <option>Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="message" className="text-sm font-medium text-zinc-600">Message *</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            required
                                            rows={5}
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full bg-base-100 border border-zinc-200 rounded px-4 py-3 text-base-content placeholder-zinc-400 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all resize-none"
                                            placeholder="Tell us about your security needs..."
                                            disabled={isSubmitting}
                                        ></textarea>
                                    </div>

                                    {error && (
                                        <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded text-sm">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-content px-8 py-4 text-xs font-bold uppercase tracking-widest rounded active:scale-95 transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                Send Message
                                                <FiSend className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>

                    </div>
                </section>
            </FadeInSection>
        </div>
    );
}

export default ContactPage;